import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  buildSheinComplianceChildPath,
  buildSheinPath,
  buildSheinProductChildPath,
} from '@/shein-platform/constants/sheinRoutes';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { readHomeLocalTodoSnapshot, readHomeLocalTodoSnapshotsAggregate } from '@/shein-platform/utils/sheinHomeLocalSnapshot';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

const TODO_CARDS = [
  {
    id: 'certificateMissing',
    titleKey: 'certMissing',
    descKey: 'certMissingDesc',
    countKey: 'certificateMissing',
    path: 'product-qual',
    query: 'dim=certificate&filter=0',
  },
  {
    id: 'labelPending',
    titleKey: 'photoPending',
    descKey: 'photoPendingDesc',
    countKey: 'labelPending',
    path: 'product-qual',
    query: 'dim=photo&filter=pending',
  },
  {
    id: 'agencyUnbound',
    titleKey: 'agencyPending',
    descKey: 'agencyPendingDesc',
    countKey: 'agencyUnbound',
    path: 'product-qual',
    query: 'dim=agency&bindStatus=unbound',
  },
  {
    id: 'certificateExpiring',
    titleKey: 'expiring',
    descKey: 'expiringDesc',
    countKey: 'certificateExpiring',
    path: 'product-qual',
    query: 'dim=certificate&filter=expiring',
  },
];

/** 会更新本地商品/合规库的同步类型：完成后首页重读本地 */
const HOME_LOCAL_SYNC_KINDS = new Set([
  'product',
  'publishRecord',
  'certificate',
  'certificateLibrary',
  'compliance',
]);

const ANALYSIS_PRODUCT_METRICS = [
  { key: 'listed', labelKey: 'platformHome.listed', tone: 'ok', path: '' },
  { key: 'pending', labelKey: 'platformHome.pendingList', tone: 'warn', path: '' },
  { key: 'soldOut', labelKey: 'platformHome.soldOut', tone: 'muted', path: '' },
  { key: 'offline', labelKey: 'platformHome.offline', tone: 'muted', path: '' },
  { key: 'drafts', labelKey: 'platformHome.draft', tone: 'info', path: 'drafts' },
  { key: 'priceReview', labelKey: 'platformHome.shein.priceReview', tone: 'alert', path: '' },
];

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickLatestSyncAt(modules = []) {
  const times = modules
    .filter((m) => {
      const mod = String(m?.module || '').toUpperCase();
      return mod === 'CERTIFICATE' || mod === 'COMPLIANCE' || mod === 'PRODUCT';
    })
    .map((m) => m?.lastSyncEndAt || m?.lastRunAt)
    .filter(Boolean)
    .map((v) => ({ raw: String(v), ts: new Date(v).getTime() }))
    .filter((item) => !Number.isNaN(item.ts))
    .sort((a, b) => b.ts - a.ts);
  return times[0]?.raw || '';
}

function AnalysisBar({ label, value, max, tone = 'info', onClick, disabled }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <button
      type="button"
      className={`spo-home-bar spo-home-bar--${tone}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="spo-home-bar__meta">
        <span className="spo-home-bar__label">{label}</span>
        <span className="spo-home-bar__value">{value}</span>
      </span>
      <span className="spo-home-bar__track" aria-hidden>
        <span className="spo-home-bar__fill" style={{ width: `${pct}%` }} />
      </span>
    </button>
  );
}

function sumComplianceSummaries(rows = []) {
  const keys = [
    'certificateMissing',
    'labelPending',
    'agencyUnbound',
    'certificateExpiring',
    'labelScannedSkc',
    'labelTotalSkcCached',
  ];
  const out = Object.fromEntries(keys.map((k) => [k, 0]));
  let labelLocalOnly = true;
  let labelScanPartial = false;
  let any = false;
  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    any = true;
    keys.forEach((k) => {
      out[k] += num(row[k]);
    });
    if (!row.labelLocalOnly) labelLocalOnly = false;
    if (row.labelScanPartial) labelScanPartial = true;
  });
  if (!any) return null;
  return {
    ...out,
    labelLocalOnly,
    labelScanPartial,
    source: 'local',
  };
}

export default function SheinHomePage() {
  const { t } = useTranslation('workbench');
  const {
    activeShop,
    expectedShopType,
    scopedShops = [],
    concreteScopeId,
    allShopsScope,
    listReady,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || '';
  const scopedShopIds = useMemo(
    () => (scopedShops || []).map((s) => s?.id).filter((id) => id != null).map(String),
    [scopedShops],
  );
  const viewShop = useMemo(() => {
    if (!shopId) return null;
    return (scopedShops || []).find((s) => String(s.id) === String(shopId)) || null;
  }, [scopedShops, shopId]);
  const caps = sheinShopCapabilities(viewShop || activeShop || expectedShopType);
  const canDiscuss = Boolean(caps.canDiscussCost);
  const navigate = useNavigate();
  const { tracks } = useSheinSyncJobs();

  const [summary, setSummary] = useState(null);
  const [listingStats, setListingStats] = useState(null);
  const [priceSnapshot, setPriceSnapshot] = useState(() => readHomeLocalTodoSnapshot(''));
  const [loading, setLoading] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState('');
  const loadSeqRef = useRef(0);
  const prevTrackStatusRef = useRef(new Map());

  /** 首页只读本地：合规汇总 / 商品统计 / 同步状态 / 价格模块回写的本地快照。绝不打平台 OpenAPI。 */
  const loadLocalHome = useCallback(async ({ silent = false } = {}) => {
    if (!listReady) {
      setSummary(null);
      setListingStats(null);
      setPriceSnapshot(readHomeLocalTodoSnapshot(''));
      setPersistedSyncedAt('');
      return;
    }
    const seq = ++loadSeqRef.current;
    if (!silent) setLoading(true);
    try {
      if (allShopsScope) {
        const [statsRes, complianceRows, syncRows] = await Promise.all([
          sheinPlatformAPI.getProductListingStats({
            allOfType: true,
            shopType: expectedShopType,
          }).catch(() => null),
          Promise.all(
            scopedShopIds.map((id) => sheinPlatformAPI.getComplianceTodoSummary(id, { source: 'local' })
              .catch(() => null)),
          ),
          Promise.all(
            scopedShopIds.map((id) => sheinPlatformAPI.getShopSyncStatus(id).catch(() => null)),
          ),
        ]);
        if (seq !== loadSeqRef.current) return;
        setSummary(sumComplianceSummaries(complianceRows) || {});
        setListingStats(statsRes || null);
        setPriceSnapshot(readHomeLocalTodoSnapshotsAggregate(scopedShopIds));
        const modules = syncRows.flatMap((res) => (
          Array.isArray(res?.modules) ? res.modules : []
        ));
        setPersistedSyncedAt(pickLatestSyncAt(modules));
        setLastRefreshedAt(new Date().toISOString());
        return;
      }

      const [complianceRes, statsRes, syncRes] = await Promise.all([
        sheinPlatformAPI.getComplianceTodoSummary(shopId, { source: 'local' }).catch((e) => {
          if (!silent) showToast(e?.message || t('platformHome.shein.loadComplianceFailed'), 'error');
          return null;
        }),
        sheinPlatformAPI.getProductListingStats(shopId).catch(() => null),
        sheinPlatformAPI.getShopSyncStatus(shopId).catch(() => null),
      ]);
      if (seq !== loadSeqRef.current) return;

      setSummary(complianceRes || {});
      setListingStats(statsRes || null);
      setPriceSnapshot(readHomeLocalTodoSnapshot(shopId));
      setPersistedSyncedAt(pickLatestSyncAt(Array.isArray(syncRes?.modules) ? syncRes.modules : []));
      setLastRefreshedAt(new Date().toISOString());
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [listReady, allShopsScope, expectedShopType, scopedShopIds, shopId, t]);

  useEffect(() => {
    loadLocalHome();
  }, [loadLocalHome]);

  // 切回标签页：重读本地库与本地快照（不打平台）
  useEffect(() => {
    if (!listReady) return undefined;
    const refresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      loadLocalHome({ silent: true });
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onLocalTodo = (event) => {
      const eventShopId = String(event?.detail?.shopId || '');
      if (allShopsScope) {
        if (eventShopId && !scopedShopIds.includes(eventShopId)) return;
        setPriceSnapshot(readHomeLocalTodoSnapshotsAggregate(scopedShopIds));
        return;
      }
      if (eventShopId !== String(shopId)) return;
      setPriceSnapshot(event.detail.snapshot || readHomeLocalTodoSnapshot(shopId));
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('ipe-shein-home-local-todo', onLocalTodo);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('ipe-shein-home-local-todo', onLocalTodo);
    };
  }, [listReady, allShopsScope, scopedShopIds, shopId, loadLocalHome]);

  // 商品/合规等本地库同步成功后，重读本地
  useEffect(() => {
    if (!listReady) return;
    const prev = prevTrackStatusRef.current;
    let shouldReload = false;
    tracks.forEach((t) => {
      const trackShopId = String(t?.shopId || '');
      if (allShopsScope) {
        if (trackShopId && !scopedShopIds.includes(trackShopId)) return;
      } else if (trackShopId !== String(shopId)) {
        return;
      }
      if (!HOME_LOCAL_SYNC_KINDS.has(t?.kind)) return;
      const id = String(t.id || '');
      const status = String(t.status || '');
      const before = prev.get(id);
      if (before && before !== 'success' && status === 'success') {
        shouldReload = true;
      }
      prev.set(id, status);
    });
    if (shouldReload) {
      loadLocalHome({ silent: true });
    }
  }, [tracks, listReady, allShopsScope, scopedShopIds, shopId, loadLocalHome]);

  const goComplianceCard = (card) => {
    navigate(`${buildSheinComplianceChildPath(card.path)}?${card.query}`);
  };

  const goProductMetric = (metric) => {
    if (metric.path === 'drafts') {
      navigate(buildSheinProductChildPath('drafts'));
      return;
    }
    navigate(buildSheinProductChildPath(''));
  };

  const complianceTotal = useMemo(() => (
    TODO_CARDS.reduce((sum, card) => {
      if (card.id === 'labelPending' && summary?.labelLocalOnly) return sum;
      return sum + num(summary?.[card.countKey]);
    }, 0)
  ), [summary]);

  const discussCount = priceSnapshot.discussPending == null ? null : num(priceSnapshot.discussPending);
  const rrpPendingAudit = priceSnapshot.rrpPendingAudit == null ? null : num(priceSnapshot.rrpPendingAudit);
  const rrpAuditFailed = priceSnapshot.rrpAuditFailed == null ? null : num(priceSnapshot.rrpAuditFailed);
  const rrpCount = (rrpPendingAudit == null && rrpAuditFailed == null)
    ? null
    : num(rrpPendingAudit) + num(rrpAuditFailed);
  const priceTodoTotal = num(discussCount) + num(rrpCount);
  const openTodoTotal = complianceTotal + priceTodoTotal;
  const hasPriceSnapshot = Boolean(priceSnapshot.updatedAt);

  const productTotal = useMemo(() => {
    if (listingStats?.total != null) return num(listingStats.total);
    return ANALYSIS_PRODUCT_METRICS.reduce((sum, m) => sum + num(listingStats?.[m.key]), 0);
  }, [listingStats]);

  const productMax = useMemo(() => (
    Math.max(1, ...ANALYSIS_PRODUCT_METRICS.map((m) => num(listingStats?.[m.key])))
  ), [listingStats]);

  const formatMaybe = (value) => {
    if (loading && summary == null && listingStats == null) return '—';
    if (value == null) return '—';
    return value;
  };

  return (
    <ProductListCard tabs={shopDimensionTabs}>
      <div className="spo-home">
        <section className="spo-home-block spo-home-section" aria-labelledby="spo-home-analysis-title">
          <div className="spo-home-section__head">
            <h2 id="spo-home-analysis-title" className="spo-home-section__title">{t('platformHome.analysis')}</h2>
            {allShopsScope && scopedShopIds.length > 0 ? (
              <p className="spo-home-section__sub">{t('platformHome.scopeSummary', { count: scopedShopIds.length })}</p>
            ) : null}
          </div>

          <div className="spo-home-kpi" role="list">
            <div className="spo-home-kpi__card" role="listitem">
              <span className="spo-home-kpi__label">{t('platformHome.shein.openTodos')}</span>
              <strong className={`spo-home-kpi__value${openTodoTotal > 0 ? ' is-alert' : ''}`}>
                {loading && summary == null ? '—' : openTodoTotal}
              </strong>
              <span className="spo-home-kpi__hint">{t('platformHome.shein.compliancePrice', { compliance: complianceTotal, price: priceTodoTotal })}</span>
            </div>
            <div className="spo-home-kpi__card" role="listitem">
              <span className="spo-home-kpi__label">{t('platformHome.spuPool')}</span>
              <strong className="spo-home-kpi__value">
                {loading && listingStats == null ? '—' : productTotal}
              </strong>
              <span className="spo-home-kpi__hint">
                {t('platformHome.onOffShelf', { listed: num(listingStats?.listed), offline: num(listingStats?.offline) })}
              </span>
            </div>
            <div className="spo-home-kpi__card" role="listitem">
              <span className="spo-home-kpi__label">{t('platformHome.shein.rrpTodos')}</span>
              <strong className={`spo-home-kpi__value${num(rrpCount) > 0 ? ' is-alert' : ''}`}>
                {formatMaybe(rrpCount)}
              </strong>
              <span className="spo-home-kpi__hint">
                {t('platformHome.shein.pendingFail', { pending: rrpPendingAudit ?? '—', failed: rrpAuditFailed ?? '—' })}
                {!hasPriceSnapshot ? t('platformHome.shein.syncPriceFirst') : ''}
              </span>
            </div>
            {canDiscuss ? (
              <div className="spo-home-kpi__card" role="listitem">
                <span className="spo-home-kpi__label">{t('platformHome.shein.discussPending')}</span>
                <strong className={`spo-home-kpi__value${num(discussCount) > 0 ? ' is-alert' : ''}`}>
                  {formatMaybe(discussCount)}
                </strong>
                <span className="spo-home-kpi__hint">
                  {discussCount == null ? t('platformHome.shein.syncDiscuss') : t('platformHome.shein.discussSnapshot')}
                </span>
              </div>
            ) : (
              <div className="spo-home-kpi__card" role="listitem">
                <span className="spo-home-kpi__label">{t('platformHome.shein.priceReviewProducts')}</span>
                <strong className={`spo-home-kpi__value${num(listingStats?.priceReview) > 0 ? ' is-alert' : ''}`}>
                  {loading && listingStats == null ? '—' : num(listingStats?.priceReview)}
                </strong>
                <span className="spo-home-kpi__hint">{t('platformHome.shein.localDimension')}</span>
              </div>
            )}
          </div>

          <div className="spo-home-analysis-grid">
            <div className="spo-home-panel">
              <h3 className="spo-home-panel__title">{t('platformHome.structure')}</h3>
              <div className="spo-home-bars">
                {ANALYSIS_PRODUCT_METRICS.map((metric) => (
                  <AnalysisBar
                    key={metric.key}
                    label={t(metric.labelKey)}
                    value={num(listingStats?.[metric.key])}
                    max={productMax}
                    tone={metric.tone}
                    disabled={!listReady}
                    onClick={() => goProductMetric(metric)}
                  />
                ))}
              </div>
            </div>
            <div className="spo-home-panel">
              <h3 className="spo-home-panel__title">{t('platformHome.shein.todoMix')}</h3>
              <ul className="spo-home-breakdown">
                <li>
                  <span>{t('platformHome.shein.compliance')}</span>
                  <strong className={complianceTotal > 0 ? 'is-alert' : ''}>{complianceTotal}</strong>
                </li>
                {canDiscuss ? (
                  <li>
                    <span>{t('platformHome.shein.discussOrders')}</span>
                    <strong className={num(discussCount) > 0 ? 'is-alert' : ''}>
                      {discussCount ?? '—'}
                    </strong>
                  </li>
                ) : null}
                <li>
                  <span>{t('platformHome.shein.rrp')}</span>
                  <strong className={num(rrpCount) > 0 ? 'is-alert' : ''}>{rrpCount ?? '—'}</strong>
                </li>
                <li>
                  <span>{t('platformHome.shein.priceReviewProducts')}</span>
                  <strong className={num(listingStats?.priceReview) > 0 ? 'is-alert' : ''}>
                    {num(listingStats?.priceReview)}
                  </strong>
                </li>
                <li>
                  <span>{t('platformHome.draftsBox')}</span>
                  <strong>{num(listingStats?.drafts)}</strong>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div className="spo-home-block spo-home-todos">
          <section className="spo-home-section" aria-labelledby="spo-home-compliance-title">
            <div className="spo-home-section__head">
              <h2 id="spo-home-compliance-title" className="spo-home-section__title">{t('platformHome.shein.complianceTitle')}</h2>
            </div>
            <div className="spo-todo-grid" role="list">
              {TODO_CARDS.map((card) => {
                const count = num(summary?.[card.countKey]);
                const labelLocalOnly = card.id === 'labelPending' && summary?.labelLocalOnly;
                const partialHint = card.id === 'labelPending' && summary?.labelScanPartial
                  ? t('platformHome.shein.scanHint', {
                    scanned: summary?.labelScannedSkc || 0,
                    total: summary?.labelTotalSkcCached || 0,
                  })
                  : '';
                const localHint = labelLocalOnly
                  ? t('platformHome.shein.localPhotoHint')
                  : '';
                return (
                  <button
                    key={card.id}
                    type="button"
                    className={`spo-todo-card${count > 0 && !labelLocalOnly ? ' spo-todo-card--alert' : ''}`}
                    role="listitem"
                    onClick={() => goComplianceCard(card)}
                    disabled={!listReady}
                  >
                    <div className="spo-todo-card__head">
                      <h3 className="spo-todo-card__title">{t(`platformHome.shein.${card.titleKey}`)}</h3>
                      <span className="spo-todo-card__count">
                        {loading && summary == null ? '—' : (labelLocalOnly ? '—' : count)}
                      </span>
                    </div>
                    <p className="spo-todo-card__desc">
                      {t(`platformHome.shein.${card.descKey}`)}
                      {partialHint}
                      {localHint}
                    </p>
                    <span className="spo-todo-card__cta">{t('platformHome.shein.goHandle')}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="spo-home-section" aria-labelledby="spo-home-discuss-title">
            <div className="spo-home-section__head">
              <h2 id="spo-home-discuss-title" className="spo-home-section__title">{t('platformHome.priceTodos')}</h2>
            </div>
            <div className="spo-todo-grid spo-todo-grid--price" role="list">
              {canDiscuss ? (
                <button
                  type="button"
                  className={`spo-todo-card${num(discussCount) > 0 ? ' spo-todo-card--alert' : ''}`}
                  role="listitem"
                  onClick={() => navigate(`${buildSheinPath('discuss/cost')}?status=1`)}
                  disabled={!listReady}
                >
                  <div className="spo-todo-card__head">
                    <h3 className="spo-todo-card__title">{t('platformHome.shein.discussConfirm')}</h3>
                    <span className="spo-todo-card__count">
                      {formatMaybe(discussCount)}
                    </span>
                  </div>
                  <p className="spo-todo-card__desc">
                    {discussCount == null
                      ? t('platformHome.shein.noSnapshot')
                      : t('platformHome.shein.discussWaiting')}
                  </p>
                  <span className="spo-todo-card__cta">{t('platformHome.shein.goDiscuss')}</span>
                </button>
              ) : null}
              <button
                type="button"
                className={`spo-todo-card${num(rrpCount) > 0 ? ' spo-todo-card--alert' : ''}`}
                role="listitem"
                onClick={() => navigate(`${buildSheinPath('discuss/rrp')}?audit=1`)}
                disabled={!listReady}
              >
                <div className="spo-todo-card__head">
                  <h3 className="spo-todo-card__title">{t('platformHome.shein.rrpTodos')}</h3>
                  <span className="spo-todo-card__count">
                    {formatMaybe(rrpCount)}
                  </span>
                </div>
                <p className="spo-todo-card__desc">
                  {t('platformHome.shein.rrpAuditLine', { pending: rrpPendingAudit ?? '—', failed: rrpAuditFailed ?? '—' })}
                  {rrpCount == null ? t('platformHome.shein.syncRrp') : ''}
                </p>
                <span className="spo-todo-card__cta">{t('platformHome.shein.goRrp')}</span>
              </button>
            </div>
            {!canDiscuss ? (
              <p className="spo-empty-hint">
                {t('platformHome.shein.selfOperatedHint')}
              </p>
            ) : null}
          </section>
        </div>

        {!listReady ? (
          <p className="spo-empty-hint">{t('platformHome.needShop', { platform: 'SHEIN' })}</p>
        ) : null}
        {listReady && !persistedSyncedAt && !lastRefreshedAt ? (
          <p className="spo-empty-hint">
            {allShopsScope
              ? t('platformHome.shein.allShopsHint', { count: scopedShopIds.length })
              : t('platformHome.shein.localComplianceHint')}
          </p>
        ) : null}
      </div>
    </ProductListCard>
  );
}
